import { ProjectDatesData } from "../../../../models";
import { SearchWorkflowTasksResponse } from "../../../../service/query";
import {
  calculateApproximateYearsAndMonthsBetween,
  convertDateToStringDDMMYYYY,
  convertDateToStringYYYYMMDD,
} from "../../../../utils";
import { ProjectRoadmapRowLabelData, ViewMode } from "../models";
import { ProjectRoadmapColourMap } from "../models/projectRoadmapConstants";

export const getFormatFromViewMode = (viewMode: ViewMode): string => {
  switch (viewMode) {
    case ViewMode.Months3:
      return "yyyy\nMMM-dd";
    case ViewMode.Months6:
    case ViewMode.Year:
      return "yyyy\nMMM";
    case ViewMode.Years3:
    case ViewMode.Years10:
      return "yyyy\nQQ";
    case ViewMode.Years20:
    default:
      return "yyyy";
  }
};

export const getProjectRoadmapRowLabelData = (data: ProjectDatesData[]): ProjectRoadmapRowLabelData => {
  return data.map((x) => ({
    displayName: x.displayName,
    uuid: x.uuid,
    status: x.currentStatus,
    additionalDates:
      x.dates.feasibility.start != null &&
      x.dates.feasibility.end != null &&
      x.dates.design.start != null &&
      x.dates.design.end != null &&
      x.dates.implementation.start != null &&
      x.dates.implementation.end != null &&
      x.dates.ongoingMonitoring.overallStart != null &&
      x.dates.ongoingMonitoring.overallEnd != null &&
      (x.dates.design?.registrationDate != null ||
        x.dates.implementation?.validationDate != null ||
        x.dates.implementation?.implementationDate != null ||
        x.dates.implementation?.startDate != null ||
        x.dates.ongoingMonitoring?.cycles[0]?.assessmentDate != null ||
        x.dates.ongoingMonitoring?.finalAssessmentDate != null),
  }));
};

const getCustomTooltipContent = (
  phase: string | undefined,
  project: string | undefined,
  startDate: Date | undefined,
  endDate: Date | undefined,
  projectUuid: string | undefined,
  isMilestone = false
): string => {
  const duration =
    startDate && endDate && startDate.getTime() !== endDate.getTime()
      ? calculateApproximateYearsAndMonthsBetween(startDate, endDate)
      : undefined;

  return `<div class="CustomTooltip">
            <div class="TooltipHeader">
              <p class="body2">
                <b>${project}</b>  
              </p>
              <div class="CloseTooltipButton" 
                onclick="window.dispatchEvent(new Event('closeTooltip'));"
              >
                <p class="body1">
                  <b>x</b>
                </p>
              </div>
            </div>
            <div class="TooltipContent">
              <p class="body2">
                <b>${phase}</b>
              </p>
              ${
                !duration && !!startDate
                  ? `<p class="body2">${isMilestone ? `<b>Deadline: </b>` : ""}
                ${convertDateToStringDDMMYYYY(startDate)}      
              </p>`
                  : ""
              }  
              ${
                !!duration && !!startDate && !!endDate
                  ? `<p class="body2">
                <b>Start/end:</b> ${convertDateToStringDDMMYYYY(startDate)} - ${convertDateToStringDDMMYYYY(endDate)}       
              </p>`
                  : ""
              }
              ${
                (!!duration?.years && duration?.years > 0) || (!!duration?.months && duration?.months > 0)
                  ? `<p class="body2">
                        <b>Duration:</b> ${duration.years > 0 ? `${duration.years} year${duration.years > 1 ? "s" : ""} ` : ""}${duration.months > 0 ? `${duration.months} month${duration.months > 1 ? "s" : ""}` : ""}
                      </p>`
                  : ""
              }
              ${
                projectUuid
                  ? `<div class="EditDatesButton" 
                          onclick="history.pushState(null,'','${window.location.pathname}?projectUuid=${projectUuid}');
                                   window.dispatchEvent(new Event('pushState'));"
                     >
                      <p class="body2"}>
                        Edit dates
                      </p>
                    </div>`
                  : ""
              }
            </div>
          </div>`;
};

export const getProjectRoadmapTimelineData = (data: ProjectDatesData[]): unknown[] => {
  const format: unknown[] = [
    [
      { type: "string", id: "Project" },
      { type: "string", id: "dummy bar label" },
      { type: "string", role: "tooltip" },
      { type: "string", role: "style" },
      { type: "date", id: "Start" },
      { type: "date", id: "End" },
    ],
  ];

  const createRow = (
    displayName: string | undefined,
    phase: string,
    tooltip: string | undefined,
    colorMap: string,
    startDate: Date | undefined,
    endDate: Date | undefined
  ): (string | Date | undefined)[] | undefined =>
    startDate && endDate ? [displayName, phase, tooltip, colorMap, startDate, endDate] : undefined;

  const rows = data.map((x) => {
    const projectRows = [
      createRow(
        x.displayName,
        "Feasibility",
        getCustomTooltipContent(
          "Feasibility",
          x.displayName,
          x.dates.feasibility?.start,
          x.dates.feasibility?.end,
          x.uuid
        ),
        ProjectRoadmapColourMap.Feasibility,
        x.dates.feasibility?.start,
        x.dates.feasibility?.end
      ),
      createRow(
        x.displayName,
        "Design",
        getCustomTooltipContent("Design", x.displayName, x.dates.design?.start, x.dates.design?.end, x.uuid),
        ProjectRoadmapColourMap.Design,
        x.dates.design?.start,
        x.dates.design?.end
      ),
      createRow(
        x.displayName,
        "Registration date",
        getCustomTooltipContent(
          "Registration date",
          x.displayName,
          x.dates.design?.registrationDate,
          x.dates.design?.registrationDate,
          x.uuid
        ),
        ProjectRoadmapColourMap["Registration date"],
        x.dates.design?.registrationDate,
        x.dates.design?.registrationDate
      ),
      createRow(
        x.displayName,
        "Implementation",
        getCustomTooltipContent(
          "Implementation",
          x.displayName,
          x.dates.implementation?.start,
          x.dates.implementation?.end,
          x.uuid
        ),
        ProjectRoadmapColourMap.Implementation,
        x.dates.implementation?.start,
        x.dates.implementation?.end
      ),
      createRow(
        x.displayName,
        "Project start date",
        getCustomTooltipContent(
          "Project start date",
          x.displayName,
          x.dates.implementation?.startDate,
          x.dates.implementation?.startDate,
          x.uuid
        ),
        ProjectRoadmapColourMap["Project start date"],
        x.dates.implementation?.startDate,
        x.dates.implementation?.startDate
      ),
      createRow(
        x.displayName,
        "Project implementation date",
        getCustomTooltipContent(
          "Project implementation date",
          x.displayName,
          x.dates.implementation?.implementationDate,
          x.dates.implementation?.implementationDate,
          x.uuid
        ),
        ProjectRoadmapColourMap["Project implementation date"],
        x.dates.implementation?.implementationDate,
        x.dates.implementation?.implementationDate
      ),
      createRow(
        x.displayName,
        "Restoration validation",
        getCustomTooltipContent(
          "Restoration validation",
          x.displayName,
          x.dates.implementation?.restorationValidationDate,
          x.dates.implementation?.restorationValidationDate,
          x.uuid
        ),
        ProjectRoadmapColourMap["Restoration validation date"],
        x.dates.implementation?.restorationValidationDate,
        x.dates.implementation?.restorationValidationDate
      ),
      createRow(
        x.displayName,
        "Certification date",
        getCustomTooltipContent(
          "Certification date",
          x.displayName,
          x.dates.implementation?.certificationDate,
          x.dates.implementation?.certificationDate,
          x.uuid
        ),
        ProjectRoadmapColourMap["Certification date"],
        x.dates.implementation?.certificationDate,
        x.dates.implementation?.certificationDate
      ),
      createRow(
        x.displayName,
        "Validation date",
        getCustomTooltipContent(
          "Validation date",
          x.displayName,
          x.dates.implementation?.validationDate,
          x.dates.implementation?.validationDate,
          x.uuid
        ),
        ProjectRoadmapColourMap["Validation date"],
        x.dates.implementation?.validationDate,
        x.dates.implementation?.validationDate
      ),
      createRow(
        x.displayName,
        "Ongoing monitoring",
        getCustomTooltipContent(
          "Ongoing monitoring",
          x.displayName,
          x.dates.ongoingMonitoring?.overallStart,
          x.dates.ongoingMonitoring?.overallEnd,
          x.uuid
        ),
        ProjectRoadmapColourMap["Ongoing monitoring"],
        x.dates.ongoingMonitoring?.overallStart,
        x.dates.ongoingMonitoring?.overallEnd
      ),
      createRow(
        x.displayName,
        "Second assessment date",
        getCustomTooltipContent(
          "Second assessment date",
          x.displayName,
          x.dates.ongoingMonitoring?.cycles[0]?.assessmentDate,
          x.dates.ongoingMonitoring?.cycles[0]?.assessmentDate,
          x.uuid
        ),
        ProjectRoadmapColourMap["Second assessment date"],
        x.dates.ongoingMonitoring?.cycles[0]?.assessmentDate,
        x.dates.ongoingMonitoring?.cycles[0]?.assessmentDate
      ),
      createRow(
        x.displayName,
        "Final assessment date",
        getCustomTooltipContent(
          "Final assessment date",
          x.displayName,
          x.dates.ongoingMonitoring?.finalAssessmentDate,
          x.dates.ongoingMonitoring?.finalAssessmentDate,
          x.uuid
        ),
        ProjectRoadmapColourMap["Final assessment date"],
        x.dates.ongoingMonitoring?.finalAssessmentDate,
        x.dates.ongoingMonitoring?.finalAssessmentDate
      ),
    ].filter(Boolean);

    // Group milestones by deadline date
    const groupedMilestones = x.milestones?.reduce(
      (acc: { [key: string]: SearchWorkflowTasksResponse["results"] }, milestone) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const dateKey = convertDateToStringYYYYMMDD(milestone.deadlineDate)!;
        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push(milestone);
        return acc;
      },
      {}
    );

    // Add row for each milestone,
    if (groupedMilestones) {
      Object.values(groupedMilestones).forEach((milestones) =>
        milestones.forEach((milestone) => {
          projectRows.push(
            createRow(
              x.displayName,
              milestones.map((m) => m.name).join(", ") ?? "",
              getCustomTooltipContent(
                milestones.map((m) => m.name).join(", ") ?? "",
                x.displayName,
                milestone.deadlineDate ?? undefined,
                milestone.deadlineDate ?? undefined,
                x.uuid,
                true
              ),
              ProjectRoadmapColourMap[`${milestone.step.phase} milestone`],
              milestone.deadlineDate ?? undefined,
              milestone.deadlineDate ?? undefined
            )
          );
        })
      );
    }

    return projectRows;
  });

  format.push(...rows.flat(1));

  return format;
};

export const calculateViewMode = (viewMode: ViewMode, data: ProjectDatesData[]): string => {
  // --- List of all chart dates ---
  const dates = data.flatMap((x) =>
    Object.values(x.dates)
      .filter((value) => (value.start && value.end) || (value.overallStart && value.overallEnd))
      .map((value) => ({
        startDate: value.start ? value.start.getTime() : value.overallStart.getTime(),
        endDate: value.end ? value.end.getTime() : value.overallEnd.getTime(),
      }))
  );

  const milestoneDates = data.flatMap((x) =>
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    x
      .milestones!.filter((value) => value.deadlineDate)
      .map((value) => ({
        startDate: value.deadlineDate,
        endDate: value.deadlineDate,
      }))
  );

  dates.push(...milestoneDates);

  // --- Calculate start, end, and marker dates ---
  const startDate = new Date(Math.min(...dates.map((x) => x.startDate)));
  const endDate = new Date(Math.max(...dates.map((x) => x.endDate)));

  const dateRangeInDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);

  // Define pixels per day for each view mode
  const pixelsPerDayMap: Record<ViewMode, number> = {
    [ViewMode.Months3]: 11,
    [ViewMode.Months6]: 5.5,
    [ViewMode.Year]: 2.75,
    [ViewMode.Years3]: 0.915,
    [ViewMode.Years10]: 0.275,
    [ViewMode.Years20]: 0.1375,
  };

  const pixelsPerDay = pixelsPerDayMap[viewMode] ?? 50;
  const width = dateRangeInDays * pixelsPerDay;

  return `${width}px`;
};
